import React, { useState } from "react";
import { Star } from "lucide-react";

interface StarRatingProps {
  value: number;
  onChange: (rating: number) => void;
  className?: string;
  readonly?: boolean;
}

export const StarRating: React.FC<StarRatingProps> = ({
  value,
  onChange,
  className = "",
  readonly = false,
}) => {
  const [hoverValue, setHoverValue] = useState<number>(0);

  const handleClick = (rating: number) => {
    if (!readonly) {
      onChange(rating);
    }
  };

  const handleMouseEnter = (rating: number) => {
    if (!readonly) {
      setHoverValue(rating);
    }
  };

  const handleMouseLeave = () => {
    if (!readonly) {
      setHoverValue(0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (readonly) return;

    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      const newRating = Math.min(5, index + 1);
      onChange(newRating);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      const newRating = Math.max(1, index - 1);
      onChange(newRating);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onChange(index);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`} role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((rating) => {
        const isFilled = (hoverValue || value) >= rating;
        const isHovered = hoverValue >= rating;

        return (
          <button
            key={rating}
            type="button"
            onClick={() => handleClick(rating)}
            onMouseEnter={() => handleMouseEnter(rating)}
            onMouseLeave={handleMouseLeave}
            onKeyDown={(e) => handleKeyDown(e, rating)}
            disabled={readonly}
            className={`
              transition-all duration-150 ease-in-out
              outline-none focus:outline-none focus-visible:outline-none
              border-0 focus:border-0 focus-visible:border-0
              ring-0 focus:ring-0 focus-visible:ring-0
              shadow-none focus:shadow-none
              rounded
              ${readonly ? "cursor-default" : "cursor-pointer hover:scale-110"}
              ${!readonly && "active:scale-95"}
            `}
            role="radio"
            aria-checked={value === rating}
            aria-label={`${rating} star${rating > 1 ? "s" : ""}`}
            tabIndex={readonly ? -1 : 0}
          >
            <Star
              className={`
                w-8 h-8 transition-colors duration-150
                ${isFilled
                  ? isHovered
                    ? "fill-[#9d5bc4] stroke-[#9d5bc4]"
                    : "fill-[#8437bb] stroke-[#8437bb]"
                  : "fill-none stroke-gray-300"
                }
              `}
            />
          </button>
        );
      })}
    </div>
  );
};
